import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Icon, SelectableCard, Skeleton } from '@tb/ui';
import { isIconName, type IconName } from '@tb/ui';
import type { CreateSiteRequest } from '@tb/shared';
import { api } from '../api/endpoints';
import { useWalletContext } from '../auth/WalletProvider';
import { CustomCodeCard } from '../components/CustomCodeCard';
import { Screen } from '../components/Layout';
import { useT } from '../i18n/LocaleProvider';
import { useManifestText } from '../i18n/useManifestText';

type Choice = { kind: 'TEMPLATE'; code: string } | { kind: 'CUSTOM' };

export function NewSiteScreen() {
  const { domain = '' } = useParams();
  const navigate = useNavigate();
  const wallet = useWalletContext();
  const t = useT();
  const m = useManifestText();
  const [choice, setChoice] = useState<Choice | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.templates.list(),
    staleTime: 5 * 60_000,
  });

  const createMutation = useMutation({
    mutationFn: (request: CreateSiteRequest) => {
      if (wallet.status !== 'ready') throw new Error('wallet not connected');
      return api.sites.create(wallet.wallet.address, request);
    },
    onSuccess: (site) => void navigate(`/sites/${site.id}/edit`, { replace: true }),
  });

  const start = () => {
    if (!choice) return;
    createMutation.mutate(
      choice.kind === 'TEMPLATE'
        ? { domain, kind: 'TEMPLATE', templateCode: choice.code }
        : { domain, kind: 'CUSTOM' },
    );
  };

  return (
    <Screen title={domain} back="/">
      <div className="tb-screen">
        <div className="tb-stack" style={{ gap: 4 }}>
          <span className="tb-title">{t('new.title')}</span>
          <span className="tb-subtitle">{t('new.subtitle')}</span>
        </div>

        {createMutation.isError ? (
          <Alert tone="danger" title={t('new.error')}>
            {(createMutation.error as Error).message}
          </Alert>
        ) : null}

        <section className="tb-stack">
          <span className="tb-label">{t('new.templates')}</span>
          {templatesQuery.isPending ? (
            <>
              <Skeleton height={78} />
              <Skeleton height={78} />
              <Skeleton height={78} />
            </>
          ) : templatesQuery.isError ? (
            <Alert tone="danger">{t('new.templatesFailed')}</Alert>
          ) : (
            <div className="template-grid">
              {templatesQuery.data.map((template) => (
                <SelectableCard
                  key={template.code}
                  icon={toIconName(template.icon)}
                  title={m(template.name)}
                  description={m(template.description)}
                  selected={choice?.kind === 'TEMPLATE' && choice.code === template.code}
                  onClick={() => setChoice({ kind: 'TEMPLATE', code: template.code })}
                />
              ))}
            </div>
          )}
        </section>

        <section className="tb-stack">
          <span className="tb-label">{t('new.custom.section')}</span>
          <CustomCodeCard
            selected={choice?.kind === 'CUSTOM'}
            description={t('new.custom.body')}
            onSelect={() => setChoice({ kind: 'CUSTOM' })}
          />
        </section>

        <div className="tb-actionbar">
          <Button
            block
            icon="chevron-right"
            disabled={!choice || wallet.status !== 'ready'}
            loading={createMutation.isPending}
            onClick={start}
          >
            {t('new.continue')}
          </Button>
        </div>

        {wallet.status !== 'ready' ? (
          <div className="tb-row" style={{ justifyContent: 'center' }}>
            <Icon name="warning" size={14} />
            <span className="tb-hint">{t('new.needWallet')}</span>
          </div>
        ) : null}
      </div>
    </Screen>
  );
}

function toIconName(value: string): IconName {
  return isIconName(value) ? value : 'template';
}
